import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { message } from 'antd';
import dd from 'dingtalk-jsapi';
import { API_BASE_URL, CORP_ID, AGENT_ID } from '../config';

// 创建认证上下文
const AuthContext = createContext();

// 认证状态管理 Hook
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// 认证提供者组件
export const AuthProvider = ({ children }) => {
  const [tokenExpiry, setTokenExpiry] = useState(0);
  const [currentUser, setCurrentUser] = useState(null);
  const [ddConfigReady, setDdConfigReady] = useState(false);
  const [outTrackId, setOutTrackId] = useState(null);
  const [actionId, setActionId] = useState(null);
  const [u2, setU2] = useState(null);
  const authInitialized = useRef(false);

  // 获取签名 - 通过后端API
  const getSignature = async () => {
    try {
      // 获取当前页面URL（去除#及后面部分）
      const url = window.location.href.split('#')[0];
      console.log('当前URL:', JSON.stringify({ url }));
      
      // 提取outTrackId参数
      const urlObj = new URL(url);
      const outTrackIdParam = urlObj.searchParams.get('outTrackId');
      const actionIdParam = urlObj.searchParams.get('actionId');
      const u2Param = urlObj.searchParams.get('u2');
      if (outTrackIdParam || actionIdParam || u2Param) {
        setOutTrackId(outTrackIdParam);
        setActionId(actionIdParam);
        setU2(u2Param);
        // message.info(`outTrackId: ${outTrackIdParam}`);
      }
      const response = await fetch(`${API_BASE_URL}/dingtalk/signature`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ url })
      });
      
      if (!response.ok) {
        throw new Error(`获取签名失败: ${response.status}`);
      }
      
      const data = await response.json();
      
      if (!data.success) {
        throw new Error(data.error || '获取签名失败');
      }
      
      return data;
    } catch (error) {
      console.error('获取签名出错:', error);
      message.error(`获取签名失败: ${error.message}`);
      return null;
    }
  };

  // 配置钉钉JSAPI
  const configureDingTalkJSAPI = async () => {
    // 非钉钉环境不配置
    if (dd.env.platform === 'notInDingTalk') {
      setDdConfigReady(true);
      return;
    }
    
    try {
      const signatureData = await getSignature();
      if (!signatureData) {
        throw new Error('无法获取签名数据');
      }
      
      // 执行dd.config配置
      dd.config({
        agentId: AGENT_ID,
        corpId: CORP_ID,
        timeStamp: signatureData.timestamp,
        nonceStr: signatureData.nonceStr,
        signature: signatureData.signature,
        jsApiList: [
          'biz.chat.choose',
          'biz.chat.send',
          'runtime.permission.requestAuthCode',
          'device.notification.alert',
          'device.notification.confirm',
          'chooseChat',
          // 其他需要使用的API...
        ]
      });
      
      dd.ready(() => {
        console.log('钉钉JSAPI配置成功');
        setDdConfigReady(true);
      });
      
      dd.error((err) => {
        console.error('钉钉JSAPI配置失败:', err);
        message.error(`钉钉配置失败: ${err.errorMessage || JSON.stringify(err)}`);
        // 即使配置失败，也继续执行，但某些功能可能受限
        setDdConfigReady(true);
      });
    } catch (error) {
      console.error('配置钉钉JSAPI出错:', error);
      // 即使配置失败，也继续执行，但某些功能可能受限
      setDdConfigReady(true);
    }
  };

  // 通过免登码获取用户信息
  const getUserInfoByCode = async (code) => {
    try {
      const response = await fetch(`${API_BASE_URL}/dingtalk/userinfo`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          code: code
        }),
      });
      
      if (!response.ok) {
        throw new Error(`获取用户信息失败: ${response.status}`);
      }
      
      const data = await response.json();
      
      if (data.success) {
        return data.result;
      } else {
        throw new Error(data.error || '获取用户信息失败');
      }
    } catch (error) {
      message.error(`获取用户信息失败: ${error.message}`);
      return null;
    }
  };

  // 授权失败时的处理函数
  const handleAuthError = () => {
    message.warning('使用默认用户模式');
    setCurrentUser({
      name: '测试用户',
      avatar: 'https://picsum.photos/id/1025/200',
      userId: 'test123'
    });
  };

  // 初始化 - 钉钉免登流程
  useEffect(() => {
    const initDingTalkAuth = async () => {
      // 防止重复初始化
      if (authInitialized.current) {
        return;
      }
      authInitialized.current = true;
      
      // 先配置钉钉JSAPI
      await configureDingTalkJSAPI();
      
      try {
        // 检查是否在钉钉环境
        if (dd.env.platform !== 'notInDingTalk') {
          // 等待dd.config完成
          if (!ddConfigReady) {
            // 可以添加一个延迟或重试机制
            await new Promise(resolve => setTimeout(resolve, 500));
          }
          
          // 获取免登授权码
          const requestAuthCode = () => {
            dd.runtime.permission.requestAuthCode({
              corpId: CORP_ID,
              onSuccess: async (result) => {
                // message.success('获取免登授权码成功');
                
                // 使用授权码获取用户信息
                const userr = await getUserInfoByCode(result.code);
                const a = await fetch(`${API_BASE_URL}/dingtalk/user/detail`, {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                  },
                  body: JSON.stringify({
                    userid: userr.userid,
                    language: 'zh_CN'
                  })
                });
                const b = await a.json();
                const userInfo = b.data.result;
                console.log("userInfo", userInfo)
                const c = await fetch(`${API_BASE_URL}/dingding_user/update`, {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                  },
                  body: JSON.stringify({
                    du_userid: userInfo.userid,
                    du_name: userInfo.name,
                    du_avatar: userInfo.avatar,
                  })
                });
                if (userInfo) {
                  setCurrentUser({
                    name: userInfo.name || '钉钉用户',
                    avatar: userInfo.avatar || `https://picsum.photos/id/${Math.floor(Math.random() * 100)}/200`,
                    userId: userInfo.userid,
                    unionId: userInfo.unionid
                  });
                  // message.success(`欢迎，${userInfo.name}`);
                } else {
                  handleAuthError();
                }
              },
              onFail: (err) => {
                console.error('获取免登授权码失败:', err);
                message.error(`免登授权失败: ${err.errorMessage || err.message}`);
                handleAuthError();
              }
            });
          };
          
          // 只在dd.ready时执行一次
          if (ddConfigReady) {
            requestAuthCode();
          } else {
            dd.ready(() => {
              requestAuthCode();
            });
          }
        } else {
          // 非钉钉环境，使用测试用户
          handleAuthError();
        }
      } catch (error) {
        console.error('初始化钉钉授权失败:', error);
        handleAuthError();
      } finally {
        const savedPosts = localStorage.getItem('valueStarPosts');
        if (savedPosts) {
          // 这里可以设置 posts 状态，但需要通过 props 或其他方式传递
          // setPosts(JSON.parse(savedPosts));
        }
      }
    };

    initDingTalkAuth();
  }, []);

  // 提供的上下文值
  const contextValue = {
    currentUser,
    ddConfigReady,
    outTrackId,
    actionId,
    u2,
    configureDingTalkJSAPI
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};